import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Stack } from '@mui/material';
import { useDispatch } from 'react-redux';

// components
import { Layout } from 'src/components/layout-wrapper/Layout';
import SubscriptionPlanPopup from './SubscriptionPlanPopup';
import { PaymentPageHeader } from './components/PaymentPageHeader';
import { PlanAndPromoCode } from './components/PlanAndPromoCode';
import { PaymentSection } from './components/PaymentSection';
import { ChallengeFlowOrderSummary } from './modules/ChallengeFlowOrderSummary';
import { ChangeChallengePlanModal } from './modal/ChangeChallengePlanModal';
import ChallengeNutritionUpsellModal from './modal/ChallengeNutritionUpsellModal';
import { useChallengeNutritionUpsell } from './hooks/useChallengeNutritionUpsell';
import { useChallengeSelectionPreview } from '../subscription-new/hooks/useChallengeSelectionPreview';

// utils & types
import {
  extractErrorMessage,
  getEnhancedUserData,
  getFullPlanObj,
  isUserFromAustralia,
  useViewport,
} from 'src/lib/utils';
import { isPlanGiftEligible } from '../subscription-new/components/gift-with-purchase/eligibility';
import { Coupon } from '../deprecated/payment/types';
import { clearCoachingAddonFromSession } from 'src/util/addon-storage.util';

// styles & assets
import colors from 'src/styles/equalution/colors';
import { useAppSelector } from 'src/store/hooks';

// services & apis
import { PaymentMethod, loadStripe } from '@stripe/stripe-js';
import history from 'src/lib/history';
import moment from 'moment';
import { checkAuthentication } from 'src/lib/auth/actions';
import { decodeCookie } from 'src/lib/cookie-parser';
import {
  CHALLENGE_TIMER_START_DATE,
  CHALLENGE_TIMER_END_DATE,
  ChallengeBundleParam,
  PLANS,
  USER_STATUS,
} from '../constants';
import {
  setNoPaymentIntentAvailable,
  setProteinFlavorValue,
  setIsSupplementAdded,
  setSupplementAddedByGift,
  setIsNutritionCoachingAdded,
  setIsFutureCommencement,
  setIsPersonalCoachingAdded,
} from 'src/store/signup';
import {
  EVENTS,
  track,
  trackSignupPaymentStepViewed,
  trackSignupCheckoutSectionViewed,
  trackCheckoutUpsellViewed,
  trackCheckoutAddonToggle,
  trackSignupPaymentSubmitted,
  trackSignupPaymentFailed,
  trackSignupPurchaseCompleted,
} from 'src/util/track';
import { setPaymentProcessing } from 'src/store/payment';
import ProductService from 'src/services/product-service';
import { useShopifyVariants } from 'src/hooks/useShopifyVariants';
import {
  getNutritionCoachingPricing,
  getNutritionCoachingPriceId,
  getPersonalCoachPriceId,
  getSupplementPackPriceId,
} from '../subscription-new/utils/plans.util';
import { CHALLENGE_BUNDLE_QUERY_PARAM } from '../subscription-new/utils/challengeBundle.util';
import {
  buildTrackedPurchasePriceIds,
  buildSignupAddonPriceIds,
  shouldPurchaseSignupAddonsSeparately,
} from './utils/signupPurchase.util';
import { ChallengePlanId } from '../types';
import ProcessingOverlay from 'src/components/processing-overlay';
import { startSubscription } from './utils/startSubscription.util';

import {
  EffectivePlanObjType,
  SubscriptionStartData,
  SubscriptionStartResult,
} from './types';
import GiftCountdownBanner from '../subscription-new/components/gift-with-purchase/GiftCountdownBanner';
import { useGiftTimer } from '../subscription-new/components/gift-with-purchase/useGiftTimer';
import { useScrollToTop } from 'src/hooks/useScrollToTop';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentPage = () => {
  const { isMobile, isDesktop, width } = useViewport();

  useScrollToTop();

  // states
  const [popupVisible, setPopupVisible] = useState(false);
  const [challengePlanPopupVisible, setChallengePlanPopupVisible] =
    useState(false);

  const [coupon, setCoupon] = useState<Coupon | undefined>();
  const [submitDisabled, setSubmitDisabled] = useState(true);

  // conditional constants
  const showDivider = width > 950;

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const [totalPrice, setTotalPrice] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0 = card, 1 = afterpay

  const shippingAddressFormRef = useRef(null);

  const handleValidityChange = (isValid: boolean) => {
    setSubmitDisabled(!isValid);
  };

  const [walletPaymentIntentId, setWalletPaymentIntentId] =
    useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const isPaymentProcessing = useAppSelector(
    state => state.payment.isPaymentProcessing
  );
  const [effectivePlanObj, setEffectivePlanObj] =
    useState<EffectivePlanObjType>();

  useEffect(() => {
    if (!isPaymentProcessing) {
      setIsProcessingPayment(false);
    }
  }, [isPaymentProcessing]);

  // Clear transient coaching addon selection from session storage upon landing on payment
  useEffect(() => {
    clearCoachingAddonFromSession();
  }, []);

  // Fetch shopify variants into Redux (used by child components)
  useShopifyVariants();

  const tagId = parseInt(coupon?.metadata?.tagId || '');

  // url params
  const searchParams = new URLSearchParams(window.location.search);
  const challengePackageParam = searchParams.get(
    'challengePackage'
  ) as ChallengePlanId | null;
  const challengeBundleParam = searchParams.get(CHALLENGE_BUNDLE_QUERY_PARAM);
  const durationParam = searchParams.get('duration');
  const codeParam = searchParams.get('code');

  const isChallengePackageParam = Boolean(
    challengePackageParam || challengeBundleParam
  );
  const isTwelveMonthParam = (Number(durationParam) || 0) === 12;

  // redux selectors
  const { user, isLoading: isUserLoading } = useAppSelector(
    state => state.auth
  );
  const isPaymentIntentLoading = useAppSelector(
    state => state.payment.isPaymentIntentLoading
  );
  const isPaymentIntentError = useAppSelector(
    state => state.payment.isPaymentIntentError
  );

  const {
    availablePlans,
    selectedPlanId,
    addonWeek,
    isChallengeAccepted,
    futureCommencement,
    challengeData,
    isFitherProjectUser,
    allPlansSorted,
    noIntentAvailable,
    showFreeTrial,
    isChallengePackageFlow: isChallengeFlow,
    isPersonalCoachingAdded,
    isNutritionCoachingAdded,
    isSupplementAdded,
    isSupplementAddedByGift,
    proteinFlavor,
    shopifyVariants,
    shopifyVariantsLoaded,
  } = useAppSelector(state => state.signUp);
  const dispatch = useDispatch();

  const isChallengePackageFlow = isChallengeFlow || isChallengePackageParam;
  const hasSistemaGiftInShopify = shopifyVariants.some(
    variant => variant.variant_name?.toLowerCase().includes('sistema')
  );
  const isSistemaGiftActive =
    isUserFromAustralia({ blockChallengeBundleCheck: true }) &&
    selectedPlanId === (ChallengeBundleParam.SIX_MONTHS as unknown as string) &&
    hasSistemaGiftInShopify;

  const isTimerActive =
    moment().isSameOrAfter(moment(CHALLENGE_TIMER_START_DATE).startOf('day')) &&
    moment().isSameOrBefore(moment(CHALLENGE_TIMER_END_DATE).endOf('day'));
  const isChallengeTimerActive = isChallengePackageFlow && isTimerActive;
  const timerState = useGiftTimer(isChallengeTimerActive);

  const showShippingAddress =
    isSupplementAdded ||
    (isChallengePackageFlow &&
      (selectedPlanId === PLANS.CHALLENGE_WITH_SUPPLEMENTS ||
        isSistemaGiftActive));

  const personalCoachPriceId = getPersonalCoachPriceId(allPlansSorted);
  const nutritionCoachingPriceId = getNutritionCoachingPriceId(allPlansSorted);
  const supplementPackPriceId = getSupplementPackPriceId(allPlansSorted);

  const nutritionCoachingPrice = Number(
    getNutritionCoachingPricing(allPlansSorted)
  );
  const mainPurchaseBundlesSignupAddons =
    !noIntentAvailable && !isChallengePackageFlow;
  const signupAddonPriceIds = useMemo(
    () =>
      buildSignupAddonPriceIds({
        isNutritionCoachingAdded,
        nutritionCoachingPriceId,
        isSupplementAdded,
        supplementPackPriceId,
      }),
    [
      isNutritionCoachingAdded,
      nutritionCoachingPriceId,
      isSupplementAdded,
      supplementPackPriceId,
    ]
  );

  const giftSelectedPlan = useMemo(
    () => availablePlans.find(p => p.id === selectedPlanId) ?? null,
    [availablePlans, selectedPlanId]
  );
  const supplementInStock = shopifyVariants.some(
    v => v.is_protein_flavour === 1
  );
  const giftActive =
    isUserFromAustralia() &&
    isPlanGiftEligible(giftSelectedPlan) &&
    !isChallengePackageFlow &&
    !!supplementPackPriceId &&
    shopifyVariantsLoaded &&
    supplementInStock;
  const hasTrackedPaymentStepViewRef = useRef(false);
  const hasTrackedCheckoutUpsellViewRef = useRef(false);
  const trackedCheckoutSectionsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!shopifyVariantsLoaded) return;

    if (giftActive && !isSupplementAdded) {
      dispatch(setSupplementAddedByGift());
    } else if (!giftActive && isSupplementAdded && isSupplementAddedByGift) {
      dispatch(setIsSupplementAdded(false));
    } else if (!supplementInStock && isSupplementAdded) {
      dispatch(setIsSupplementAdded(false));
    }
  }, [
    giftActive,
    isSupplementAdded,
    isSupplementAddedByGift,
    supplementInStock,
    shopifyVariantsLoaded,
    dispatch,
  ]);

  useChallengeSelectionPreview(isChallengePackageFlow);

  useEffect(() => {
    if (isUserLoading === false && !user) {
      const cookie = decodeCookie();
      const token = cookie?.token;
      if (!token || token === 'undefined' || token === 'null') {
        history.push('/login-account', {
          from: {
            pathname: '/subscription',
            search: window.location.search,
          },
        });
      }
    }
  }, [isUserLoading, user]);

  const isChallengeFlowWithUrlCode = isChallengePackageFlow && !!codeParam;

  const showTwoStepProcess = useMemo(() => {
    const isProspectUser =
      user?.status === USER_STATUS.PROSPECT ||
      user?.status === USER_STATUS.INACTIVE;
    return isProspectUser || isFitherProjectUser;
  }, [isFitherProjectUser, user?.status]);

  useEffect(() => {
    let discountAmount = 0;
    const finalPlanObj = getFullPlanObj(
      allPlansSorted,
      selectedPlanId,
      addonWeek
    );
    if (finalPlanObj) {
      // skipNextCycle promos charge cycle 1 at full price; the
      // discount is deferred to cycle 2 server-side. Don't apply the coupon's
      // percent/amount off to the cycle-1 preview or it'll show $0 today.
      const isSkipCycle = coupon?.metadata?.skipNextCycle === 'true';
      if (coupon && !isSkipCycle) {
        if (coupon.amountOff > 0) {
          discountAmount = coupon.amountOff / 100;
        } else if (coupon.percentOff > 0) {
          discountAmount =
            parseFloat(finalPlanObj.unitAmount) * (coupon.percentOff / 100);
        }

        const remainingAmount =
          parseFloat(finalPlanObj?.unitAmount) - discountAmount > 0
            ? parseFloat(finalPlanObj?.unitAmount) - discountAmount
            : 0;
        setEffectivePlanObj({
          ...finalPlanObj,
          perCycleAmount: (
            parseFloat(remainingAmount.toString()) / finalPlanObj.intervalCount
          ).toFixed(2),
          unitAmount: remainingAmount.toString(),
        });
        setTotalPrice(remainingAmount);
      } else {
        setEffectivePlanObj(finalPlanObj);
        setTotalPrice(parseFloat(finalPlanObj.unitAmount));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon, availablePlans, selectedPlanId, addonWeek]);

  useEffect(() => {
    if (hasTrackedPaymentStepViewRef.current || !effectivePlanObj) {
      return;
    }

    trackSignupPaymentStepViewed(effectivePlanObj.id, totalPrice);
    hasTrackedPaymentStepViewRef.current = true;
  }, [effectivePlanObj, totalPrice]);

  useEffect(() => {
    if (!effectivePlanObj) {
      return;
    }

    const trackCheckoutSectionView = (sectionName: string) => {
      if (trackedCheckoutSectionsRef.current.has(sectionName)) {
        return;
      }

      trackSignupCheckoutSectionViewed(sectionName, effectivePlanObj.id);
      trackedCheckoutSectionsRef.current.add(sectionName);
    };

    trackCheckoutSectionView('order_summary');
    trackCheckoutSectionView('payment_method');

    if (showShippingAddress) {
      trackCheckoutSectionView('shipping_details');
    }
  }, [effectivePlanObj, showShippingAddress]);

  useEffect(() => {
    if (
      hasTrackedCheckoutUpsellViewRef.current ||
      isChallengePackageFlow ||
      !isUserFromAustralia()
    ) {
      return;
    }

    trackCheckoutUpsellViewed(
      giftActive || !supplementPackPriceId
        ? 'nutrition_coaching'
        : 'nutrition_coaching,supplement_pack',
      effectivePlanObj?.id
    );
    hasTrackedCheckoutUpsellViewRef.current = true;
  }, [
    effectivePlanObj,
    giftActive,
    isChallengePackageFlow,
    supplementPackPriceId,
  ]);

  const findPlanName = (planId: string): string => {
    if (planId.includes('Standard')) {
      return PLANS.STANDARD_PLAN;
    }
    if (planId.includes('Premium')) {
      return PLANS.PREMIUM_PLAN;
    }
    if (planId.includes('Daily')) {
      return PLANS.DAILY_TARGETS_ONLY_PLAN;
    }
    return '';
  };

  const StartSubscription = async (
    props: SubscriptionStartData
  ): Promise<SubscriptionStartResult> => {
    return startSubscription({
      ...props,
      isPersonalCoachingAdded,
      personalCoachPriceId,
      showFreeTrial,
      isChallengePackageFlow,
      signupAddonPriceIds,
      claimGift: giftActive,
      selectedPlanId,
      allPlansSorted,
      effectivePlanObj,
      totalPrice: totalPrice.toString(),
    });
  };

  const resetProcessingStates = useCallback(
    (resetEffectivePlan: boolean = false) => {
      dispatch(setPaymentProcessing(false));
      setIsProcessingPayment(false);
      setPaymentMethod(undefined);
      if (resetEffectivePlan) {
        setEffectivePlanObj(undefined);
      }
    },
    [dispatch]
  );

  const handleAddonPurchase = useCallback(
    async (customerId?: string): Promise<{ success: boolean }> => {
      try {
        if (
          signupAddonPriceIds.length > 0 &&
          paymentMethod &&
          customerId &&
          user
        ) {
          await ProductService.purchaseNutritionCoachingAddon({
            userId: user.id,
            email: user.email,
            paymentMethodId: paymentMethod.id,
            priceIds: signupAddonPriceIds,
            customerId,
            isAfterPay: false,
            isCardPayment: true,
            claimGift: giftActive,
          });
        }
        return { success: true };
      } catch (addonError) {
        // Log addon error and show error toast
        console.error('Addon purchase failed:', addonError);
        setErrorMessage('Failed to purchase add-ons. Please try again.');

        // Reset processing states due to addon failure
        resetProcessingStates();

        return { success: false };
      }
    },
    [
      signupAddonPriceIds,
      paymentMethod,
      user,
      resetProcessingStates,
      giftActive,
    ]
  );

  const handleEventTrackingAndNavigation = useCallback(
    (additionalPriceIds: string[] = []) => {
      const purchaseComplete =
        user!.status !== 'PROSPECT' ? 'ClientRenewal' : 'ClientNewPurchase';

      const enhancedUserData = getEnhancedUserData(user!);
      const trackedPriceIds = buildTrackedPurchasePriceIds({
        mainPriceId: effectivePlanObj?.id,
        isPersonalCoachingAdded,
        personalCoachPriceId,
        isNutritionCoachingAdded,
        nutritionCoachingPriceId,
        isSupplementAdded,
        supplementPackPriceId,
        additionalPriceIds,
      });

      const hasNutritionCoaching =
        isNutritionCoachingAdded ||
        (nutritionCoachingPriceId &&
          additionalPriceIds.includes(nutritionCoachingPriceId));

      const successUrl = new URL('/success', window.location.origin);
      successUrl.searchParams.set('ref', 'TransactionComplete');
      if (isChallengePackageFlow) {
        successUrl.searchParams.set('isChallengeFlow', 'true');
      }
      if (hasNutritionCoaching) {
        successUrl.searchParams.set('nutritionCoachingAdded', 'true');
      }

      trackSignupPurchaseCompleted(
        effectivePlanObj?.id,
        totalPrice,
        trackedPriceIds.join(',')
      );

      history.push(successUrl.pathname + successUrl.search, {
        tagId,
        futureCommencement,
        priceId: effectivePlanObj?.id,
        coupon,
        totalPrice: totalPrice.toString(),
        dataLayer: track(EVENTS.programPurchase, {
          currency: 'AUD',
          value: totalPrice,
          purchaseComplete,
          priceIDs: trackedPriceIds,
          isFitherProjectUser,
          isChallengePackageFlow,
          ...enhancedUserData,
        }),
      });

      dispatch(checkAuthentication());
    },
    [
      user,
      effectivePlanObj,
      isPersonalCoachingAdded,
      personalCoachPriceId,
      isNutritionCoachingAdded,
      nutritionCoachingPriceId,
      isSupplementAdded,
      supplementPackPriceId,
      isChallengePackageFlow,
      tagId,
      futureCommencement,
      coupon,
      totalPrice,
      isFitherProjectUser,
      dispatch,
    ]
  );

  const handleChallengeUpsellComplete = useCallback(
    (additionalPriceIds: string[] = []) => {
      resetProcessingStates(true);
      handleEventTrackingAndNavigation(additionalPriceIds);
    },
    [handleEventTrackingAndNavigation, resetProcessingStates]
  );

  const challengeNutritionUpsell = useChallengeNutritionUpsell({
    isChallengePackageFlow,
    isAustraliaUser: isUserFromAustralia(),
    nutritionCoachingPrice,
    nutritionCoachingPriceId,
    isLegacyChallengeCoachSelected: isPersonalCoachingAdded ?? false,
    user,
    onClearLegacyChallengeCoachSelection: () =>
      dispatch(setIsPersonalCoachingAdded(false)),
    onComplete: handleChallengeUpsellComplete,
    selectedPlanId,
  });

  const handleSubmit = async (): Promise<void> => {
    // prevent duplicate submissions
    if (isProcessingPayment) {
      return;
    }
    // Block while a PaymentIntent is being (re)created — confirming a stale
    // clientSecret would charge under the previous coupon/addon state.
    if (isPaymentIntentLoading) {
      resetProcessingStates();
      setErrorMessage('Updating order summary, please try again in a moment.');
      return;
    }
    setIsProcessingPayment(true);
    setErrorMessage('');
    if (!effectivePlanObj) {
      resetProcessingStates();
      return;
    }
    try {
      if (user && paymentMethod) {
        trackSignupPaymentSubmitted('card', effectivePlanObj.id, totalPrice);

        const result = await StartSubscription({
          user,
          paymentMethod,
          priceId: effectivePlanObj.id,
          coupon,
          isChallengeAccepted,
          totalPrice: totalPrice.toString(),
          futureCommencement,
          tagId,
          productId: effectivePlanObj.productId,
          challengeObject: challengeData,
          walletPaymentIntentId,
          noIntentAvailable,
        });

        if (result?.errorMsg) {
          trackSignupPaymentFailed(
            'card',
            'purchase_request',
            result.errorMsg,
            effectivePlanObj.id
          );
          setErrorMessage(result.errorMsg);
          resetProcessingStates();
        } else {
          if (
            shouldPurchaseSignupAddonsSeparately({
              bundledAddonPriceIds: signupAddonPriceIds,
              mainPurchaseBundlesAddonPriceIds: mainPurchaseBundlesSignupAddons,
              walletPaymentIntentId,
            })
          ) {
            const addonPurchaseResult = await handleAddonPurchase(
              result.customerId
            );
            if (!addonPurchaseResult.success) {
              return;
            }
          }

          if (challengeNutritionUpsell.isEligible) {
            resetProcessingStates();
            challengeNutritionUpsell.openUpsell({
              customerId: result.customerId,
              paymentMethodId: paymentMethod.id,
            });
            return;
          }

          resetProcessingStates(true);
          handleEventTrackingAndNavigation();
        }
      } else {
        dispatch(checkAuthentication());
      }
    } catch (e) {
      resetProcessingStates();
      const errorMessage = extractErrorMessage(e);
      trackSignupPaymentFailed(
        'card',
        'purchase_request',
        errorMessage,
        effectivePlanObj.id
      );
      setErrorMessage(errorMessage);
    }
  };

  const isTwelveMonthSelected: boolean = Boolean(
    effectivePlanObj &&
      effectivePlanObj?.interval === 'month' &&
      effectivePlanObj?.intervalCount === 12
  );

  // clear coupon if switching to 12-month
  useEffect(() => {
    if (isTwelveMonthSelected && coupon) {
      setCoupon(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTwelveMonthSelected]);

  // Handler for nutrition coaching CTA
  const handleNutritionCoachingAdd = useCallback(() => {
    trackCheckoutAddonToggle(true, 'nutrition_coaching', effectivePlanObj?.id);
    dispatch(setIsNutritionCoachingAdded(true));
  }, [dispatch, effectivePlanObj?.id]);

  // Handler for supplement CTA
  const handleSupplementAdd = useCallback(() => {
    trackCheckoutAddonToggle(true, 'supplement_pack', effectivePlanObj?.id);
    dispatch(setIsSupplementAdded(true));
  }, [dispatch, effectivePlanObj?.id]);

  const handleNutritionCoachingRemove = useCallback(() => {
    trackCheckoutAddonToggle(false, 'nutrition_coaching', effectivePlanObj?.id);
    dispatch(setIsNutritionCoachingAdded(false));
  }, [dispatch, effectivePlanObj?.id]);

  const handleSupplementRemove = useCallback(() => {
    trackCheckoutAddonToggle(false, 'supplement_pack', effectivePlanObj?.id);
    dispatch(setIsSupplementAdded(false));
  }, [dispatch, effectivePlanObj?.id]);

  if (!effectivePlanObj) {
    return null;
  }

  return (
    <>
      <Layout module="login" hideHeader={isMobile && popupVisible}>
        {isChallengeTimerActive && <GiftCountdownBanner timer={timerState} />}
        <Stack
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{
            padding: '1.5rem',
            marginX: 'auto',
            ...(isDesktop && { width: '1120px' }),
            ...(isMobile && { width: '320px' }),
          }}>
          <PaymentPageHeader
            isMobile={isMobile}
            showTwoStepProcess={showTwoStepProcess}
          />
          <Stack
            alignItems={'flex-end'}
            justifyContent={'center'}
            direction={'row'}
            flex={1}
            flexWrap={'wrap-reverse'}>
            <PaymentSection
              isMobile={isMobile}
              showShippingAddress={showShippingAddress}
              isChallengePackageFlow={isChallengePackageFlow}
              isSupplementAdded={isSupplementAdded ?? false}
              isSistemaGiftActive={isSistemaGiftActive}
              isNutritionCoachingAdded={isNutritionCoachingAdded ?? false}
              proteinFlavor={proteinFlavor}
              isUserLoading={isUserLoading ?? false}
              effectivePlanObj={effectivePlanObj}
              selectedPlanId={selectedPlanId}
              submitDisabled={
                submitDisabled || isPaymentIntentLoading || isPaymentIntentError
              }
              paymentMethod={paymentMethod}
              coupon={coupon}
              isChallengeAccepted={isChallengeAccepted ?? false}
              futureCommencement={futureCommencement ?? false}
              tagId={tagId}
              challengeData={challengeData}
              errorMessage={errorMessage}
              popupVisible={popupVisible}
              noIntentAvailable={noIntentAvailable ?? false}
              stripePromise={stripePromise}
              findPlanName={findPlanName}
              handleValidityChange={handleValidityChange}
              handleSubmit={handleSubmit}
              setPaymentMethod={setPaymentMethod}
              setErrorMessage={setErrorMessage}
              setWalletPaymentIntentId={setWalletPaymentIntentId}
              setNoIntentAvailable={(value: boolean) => {
                dispatch(setNoPaymentIntentAvailable(value));
              }}
              shippingAddressFormRef={shippingAddressFormRef}
              setProteinFlavorValue={value => {
                dispatch(setProteinFlavorValue(value));
              }}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              claimGift={giftActive}
            />
            {showDivider && (
              <Stack
                mx={'40px'}
                height={showShippingAddress ? '950px' : '470px'}
                borderLeft={`1px solid ${colors.OFF_BLACK}4D`}
              />
            )}
            {isChallengePackageFlow ? (
              <ChallengeFlowOrderSummary
                effectivePlanObj={effectivePlanObj}
                handleChangePlan={() => setChallengePlanPopupVisible(true)}
                coupon={coupon}
                setCoupon={setCoupon}
                isChallengeFlowWithUrlCode={isChallengeFlowWithUrlCode}
                codeParam={codeParam}
                futureCommencement={futureCommencement}
                setFutureCommencement={value =>
                  dispatch(setIsFutureCommencement(value))
                }
                isSistemaGiftActive={isSistemaGiftActive}
              />
            ) : (
              <PlanAndPromoCode
                isMobile={isMobile}
                effectivePlanObj={effectivePlanObj}
                isNutritionCoachingAdded={isNutritionCoachingAdded ?? false}
                isSupplementAdded={isSupplementAdded ?? false}
                allPlansSorted={allPlansSorted}
                handleNutritionCoachingAdd={handleNutritionCoachingAdd}
                handleSupplementAdd={handleSupplementAdd}
                onRemoveNutritionCoaching={handleNutritionCoachingRemove}
                onRemoveSupplement={handleSupplementRemove}
                handleChangePlan={() => setPopupVisible(true)}
                coupon={coupon}
                setCoupon={setCoupon}
                showFreeTrial={showFreeTrial}
                isTwelveMonthSelected={isTwelveMonthSelected}
                isFitherProjectUser={isFitherProjectUser}
                futureCommencement={futureCommencement ?? false}
                isGiftActive={giftActive}
              />
            )}
          </Stack>
          <Box height={'100px'} />
        </Stack>
      </Layout>
      {popupVisible && (
        <SubscriptionPlanPopup
          setPopupVisible={setPopupVisible}
          user={user}
          planRequest={'planRequest'}
          isTwelveMonthParam={isTwelveMonthParam}
        />
      )}
      {challengePlanPopupVisible && (
        <ChangeChallengePlanModal
          setPopupVisible={setChallengePlanPopupVisible}
        />
      )}
      <ChallengeNutritionUpsellModal
        open={challengeNutritionUpsell.isVisible}
        price={nutritionCoachingPrice}
        isSubmitting={challengeNutritionUpsell.isSubmitting}
        errorMessage={challengeNutritionUpsell.errorMessage}
        onDecline={challengeNutritionUpsell.handleDecline}
        onConfirmPurchase={challengeNutritionUpsell.handleConfirm}
      />
      {isUserLoading && <ProcessingOverlay message="Loading..." />}
    </>
  );
};

export default PaymentPage;
